import { toast } from 'sonner';

// Ratenlimit-Einstellungen
interface RateLimitConfig {
  maxRequests: number;
  timeWindowMs: number;
}

interface AIOptimizeOptions {
  content: string;
  mode: 'summarize' | 'optimize' | 'format' | 'seo' | 'title';
  maxLength?: number;
  preserveContent?: boolean;
}

class AIService {
  // Rate-Limiting-Konfiguration
  private rateLimitConfig: RateLimitConfig = {
    maxRequests: 10, // Maximale Anzahl von Anfragen
    timeWindowMs: 60 * 60 * 1000 // Zeitfenster in Millisekunden (1 Stunde)
  };

  // Speicher für das Rate-Limiting
  private requestTimestamps: number[] = [];

  constructor() {
    // Laden der gespeicherten Zeitstempel für das Rate-Limiting
    this.loadRequestTimestamps();
  }

  // Laden der gespeicherten Zeitstempel
  private loadRequestTimestamps(): void {
    try {
      const savedTimestamps = localStorage.getItem('kaufpause-ai-request-timestamps');
      if (savedTimestamps) {
        this.requestTimestamps = JSON.parse(savedTimestamps);
        // Bereinigen von alten Zeitstempeln
        this.cleanupOldTimestamps();
      }
    } catch (err) {
      console.error('Fehler beim Laden der Anfragezeitstempel:', err);
      this.requestTimestamps = [];
    }
  }

  // Speichern der Zeitstempel
  private saveRequestTimestamps(): void {
    try {
      localStorage.setItem('kaufpause-ai-request-timestamps', JSON.stringify(this.requestTimestamps));
    } catch (err) {
      console.error('Fehler beim Speichern der Anfragezeitstempel:', err);
    }
  }

  // Bereinigen alter Zeitstempel außerhalb des Zeitfensters
  private cleanupOldTimestamps(): void {
    const now = Date.now();
    const timeWindow = this.rateLimitConfig.timeWindowMs;
    this.requestTimestamps = this.requestTimestamps.filter(
      timestamp => now - timestamp < timeWindow
    );
    this.saveRequestTimestamps();
  }

  // Prüfen, ob eine neue Anfrage erlaubt ist
  private canMakeRequest(): boolean {
    this.cleanupOldTimestamps();
    return this.requestTimestamps.length < this.rateLimitConfig.maxRequests;
  }

  // Protokollieren einer neuen Anfrage
  private logRequest(): void {
    this.requestTimestamps.push(Date.now());
    this.saveRequestTimestamps();
  }

  // Anzahl der verbleibenden Anfragen innerhalb des Zeitfensters abrufen
  getRemainingRequests(): number {
    this.cleanupOldTimestamps();
    return Math.max(0, this.rateLimitConfig.maxRequests - this.requestTimestamps.length);
  }

  // Zeitpunkt, an dem das Rate-Limit zurückgesetzt wird
  getRateLimitResetTime(): Date {
    if (this.requestTimestamps.length === 0) {
      return new Date();
    }
    
    const oldestTimestamp = Math.min(...this.requestTimestamps);
    const resetTime = new Date(oldestTimestamp + this.rateLimitConfig.timeWindowMs);
    return resetTime;
  }

  // API-Schlüssel abrufen (aus Umgebungsvariablen)
  private getApiKey(): string | null {
    // Zunächst aus Umgebungsvariablen abrufen
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    
    if (!apiKey) {
      console.error('Kein OpenAI API-Schlüssel in den Umgebungsvariablen gefunden.');
      return null;
    }
    
    return apiKey;
  }

  // Prüfen, ob ein API-Schlüssel gesetzt ist
  hasApiKey(): boolean {
    return !!this.getApiKey();
  }

  // Blog-Artikel mit OpenAI optimieren
  async optimizeBlogContent({ content, mode, maxLength = 250, preserveContent = false }: AIOptimizeOptions): Promise<string> {
    const apiKey = this.getApiKey();
    
    if (!apiKey) {
      throw new Error('Kein OpenAI API-Schlüssel in den Umgebungsvariablen gefunden. Bitte füge VITE_OPENAI_API_KEY zur .env-Datei hinzu.');
    }

    // Rate-Limiting prüfen
    if (!this.canMakeRequest()) {
      const resetTime = this.getRateLimitResetTime();
      const formattedTime = resetTime.toLocaleTimeString();
      throw new Error(`Rate-Limit erreicht. Bitte versuche es nach ${formattedTime} erneut.`);
    }

    let prompt = '';
    let model = 'gpt-3.5-turbo';

    switch (mode) {
      case 'summarize':
        prompt = `Fasse den folgenden Blogartikel in maximal ${maxLength} Wörtern zusammen. Die Zusammenfassung soll die wichtigsten Punkte erfassen und als Kurzbeschreibung des Artikels dienen. Schreibe in einem sachlichen, informativen Ton. Halte die Zusammenfassung unter ${maxLength * 5} Zeichen:\n\n${content}`;
        break;
      case 'optimize':
        prompt = `Optimiere den folgenden Blogartikel. Verbessere die Formulierungen, korrigiere Grammatik- und Rechtschreibfehler, und mache den Text fließender und ansprechender. Behalte den Inhalt und die Kernaussagen bei, aber verbessere den Schreibstil für mehr Klarheit und Überzeugungskraft:\n\n${content}`;
        break;
      case 'format':
        if (preserveContent) {
          prompt = `Formatiere den folgenden Text als gut strukturierten Markdown-Blogartikel, OHNE den Inhalt umzuschreiben. Füge nur Markdown-Formatierungen hinzu wie: Überschriften (## und ###), Aufzählungspunkte, Hervorhebungen (fett/kursiv), Zitate und Links. Der eigentliche Inhalt und die Wortwahl sollen unverändert bleiben:\n\n${content}`;
        } else {
          prompt = `Formatiere den folgenden Text als gut strukturierten Markdown-Blogartikel. Füge passende Überschriften, Aufzählungen, und Hervorhebungen hinzu. Behalte den gesamten Inhalt bei, aber verbessere die Lesbarkeit und visuelle Struktur:\n\n${content}`;
        }
        break;
      case 'seo':
        prompt = `Erstelle eine SEO-optimierte Kurzbeschreibung für den folgenden Blogartikel. Die Beschreibung sollte etwa 150-180 Zeichen lang sein, Hauptschlüsselwörter enthalten und Leser zum Klicken motivieren. Fokussiere auf die Hauptvorteile oder wichtigsten Erkenntnisse des Artikels:\n\n${content}`;
        break;
      case 'title':
        prompt = `Generiere einen prägnanten, ansprechenden Titel für den folgenden Blogartikel. Der Titel sollte maximal 10 Wörter lang sein, das Hauptthema klar kommunizieren und Interesse wecken. Verwende KEINE Anführungszeichen oder andere Sonderzeichen. Gib nur den reinen Titeltext zurück ohne Formatierungen:\n\n${content}`;
        break;
      default:
        throw new Error('Ungültiger Optimierungsmodus');
    }

    try {
      // Protokollieren der Anfrage für das Rate-Limiting
      this.logRequest();

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: 'Du bist ein Experte für das Schreiben und Optimieren von Blogartikeln zum Thema bewusstes Konsumieren, Finanzen und Nachhaltigkeit. Deine Aufgabe ist es, hochwertige, ansprechende und informative Inhalte zu erstellen.' },
            { role: 'user', content: prompt }
          ],
          temperature: mode === 'title' ? 0.8 : 0.7,
          max_tokens: mode === 'summarize' || mode === 'seo' || mode === 'title' ? 500 : 2500
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI API-Fehler: ${errorData.error?.message || 'Unbekannter Fehler'}`);
      }

      const data = await response.json();
      return data.choices[0].message.content.trim();
    } catch (error) {
      console.error('Fehler bei der OpenAI API-Anfrage:', error);
      toast.error(`Fehler bei der KI-Verarbeitung: ${error instanceof Error ? error.message : 'Unbekannter Fehler'}`);
      throw error;
    }
  }
}

// Singleton-Instanz exportieren
export const aiService = new AIService(); 